import { useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "./ui/card";
import { Check, Sparkles, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";


const PLANS = [
  {
    name: "Free",
    // Het label op het scherm; `name` is ook de sleutel voor `user.plan` en de betaal-id.
    label: "Gratis",
    price: "€0",
    features: ["1 LLM connection", "Unlimited local history", "CLAW-Runtime (read-only)", "Basic Galaxy skills", "LAN P2P sync", "SPEAQ ID included (free forever)"],
    cta: "Huidig abonnement",
    disabled: true,
  },
  {
    name: "Student",
    price: "€4.90/mo",
    features: ["Unlimited LLM connections", "Council & full CLAW-Runtime", "AI OS routing + personal knowledge base", "Full Galaxy marketplace", "SPEAQ ID Pro included", "Verified students only"],
    cta: "Opwaarderen",
    priceId: "student",
  },
  {
    name: "Pro",
    price: "€9.90/mo",
    features: ["Unlimited LLM connections", "Council & full CLAW-Runtime", "AI OS routing + personal knowledge base", "Full Galaxy marketplace", "Internet-wide P2P sync", "SPEAQ ID Pro included"],
    cta: "Opwaarderen",
    priceId: "pro",
    popular: true,
  },
  {
    name: "Teams",
    price: "€49/mo",
    features: ["Everything in Pro, up to 10 seats", "Team workspaces via P2P sync", "Team knowledge base", "Compute-sharing pool", "Central billing", "SPEAQ ID Pro for every seat"],
    cta: "Opwaarderen",
    priceId: "teams",
  },
];

export default function UpgradeDialog({ open, onOpenChange, user }) {
  const { t } = useTaal();
  const [isLoading, setIsLoading] = useState(false);

  const handleUpgrade = async (priceId) => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/store/checkout`, {
        tier: priceId,
        billing: "monthly",
      });
      if (response.data.url) {
        window.open(response.data.url, "_blank");
      } else {
        toast.info(response.data.note || t("Afrekenen gestart"));
      }
    } catch (error) {
      if (error.response?.status === 501) {
        toast.info(t("Betalen is nog niet ingesteld. Komt binnenkort."));
      } else {
        toast.error(error.response?.data?.detail || t("Opwaarderen starten mislukt"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleManage = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/store/portal`, null, {
        params: { customer_id: user?.stripe_customer_id || "" },
      });
      window.open(response.data.url, "_blank");
    } catch (error) {
      toast.error(t("Het betaalportaal openen mislukt"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px]">
        <DialogHeader className="text-center">
          <div className="flex justify-center mb-2">
            <Sparkles className="h-8 w-8 text-[hsl(var(--accent))]" />
          </div>
          <DialogTitle className="font-serif text-2xl">{t("Je abonnement opwaarderen")}</DialogTitle>
          <DialogDescription>{t("Kies het abonnement dat bij je past")}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 py-4">
          {PLANS.map((plan) => (
            <Card key={plan.name} className={plan.popular ? "border-[hsl(var(--accent))] relative" : ""}>
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[hsl(var(--accent))] text-white text-xs font-medium px-3 py-0.5 rounded-full">
                  {t("Populair")}
                </div>
              )}
              <CardHeader>
                <CardTitle className="font-serif">{plan.label ? t(plan.label) : plan.name}</CardTitle>
                <CardDescription className="text-2xl font-bold text-foreground">{plan.price}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {plan.features.map((f) => (
                  <div key={f} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-ok flex-shrink-0" />
                    <span>{f}</span>
                  </div>
                ))}
              </CardContent>
              <CardFooter>
                {plan.name.toLowerCase() === (user?.plan || "free") ? (
                  <Button variant="outline" className="w-full" disabled>
                    {t("Huidig abonnement")}
                  </Button>
                ) : (
                  <Button
                    variant={plan.popular ? "default" : "outline"}
                    className="w-full"
                    disabled={isLoading || plan.disabled}
                    onClick={() => handleUpgrade(plan.priceId || plan.name.toLowerCase())}
                  >
                    {t(plan.cta)}
                  </Button>
                )}
              </CardFooter>
            </Card>
          ))}
        </div>

        {user?.plan !== "free" && (
          <div className="text-center">
            <Button variant="link" size="sm" onClick={handleManage} disabled={isLoading}>
              {t("Abonnement beheren")} <ExternalLink className="h-3 w-3 ml-1" />
            </Button>
          </div>
        )}

        <p className="text-xs text-center text-muted-foreground">
          {t("Je brengt je eigen AI mee: je eigen sleutel of gratis lokale modellen. We rekenen nooit iets op je tokens. Je betaalt alleen voor het podium, met SPEAQ ID erbij. Studenten vanaf €4,90 per maand. Altijd opzegbaar.")}
        </p>
      </DialogContent>
    </Dialog>
  );
}
